import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { EventResponse, TrainingPackageResponse, TrainingDayCapacityResponse } from '../../../../../../../../@shared/models/response.module';
import { TrainingService } from '../../../../../../../../@shared/services/training.service';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DeleteModal } from '../../../../../../../../@shared/components/delete-modal/delete-modal';
import { ToastService } from '../../../../../../../../@core/services/toast.service';

interface PackageDayForm {
  training_date: string;
  dayLabel: string;
  session_count: number;
  hasCapacity: boolean;
}

@Component({
  selector: 'app-wizard-step-packages',
  standalone: false,
  templateUrl: './wizard-step-packages.html',
  styleUrl: './wizard-step-packages.css'
})
export class WizardStepPackages implements OnInit, OnChanges {
  @Input() event: EventResponse | null = null;
  @Output() packagesChanged = new EventEmitter<number>();

  packages: TrainingPackageResponse[] = [];
  dayCapacities: TrainingDayCapacityResponse[] = [];
  loading = false;
  showAddModal = false;
  editingPackage: TrainingPackageResponse | null = null;

  // Form fields
  packageName = '';
  packageDescription = '';
  sessionCount = 1;
  sessionType: 'group' | 'individual' | 'both' = 'group';
  priceCents = 0;
  priceDisplay = '';
  currency = 'NOK';
  vatRate = 0;
  maxRegistrations: number | null = null;

  // Day distribution
  packageDays: PackageDayForm[] = [];

  sessionTypes: { value: string; label: string }[] = [];

  vatOptions = [
    { value: 0, label: '0%' },
    { value: 12.5, label: '12.5%' },
    { value: 25, label: '25%' }
  ];

  currencyOptions = [
    { code: 'NOK', name: 'NOK' },
    { code: 'SEK', name: 'SEK' },
    { code: 'EUR', name: 'EUR' },
    { code: 'USD', name: 'USD' },
    { code: 'GBP', name: 'GBP' },
    { code: 'DKK', name: 'DKK' },
  ];

  isFree = false;

  constructor(
    private trainingService: TrainingService,
    private translateService: TranslateService,
    private modalService: NgbModal,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.sessionTypes = [
      { value: 'group', label: this.translateService.instant('eventWizard.packages.typeGroup') },
      { value: 'individual', label: this.translateService.instant('eventWizard.packages.typeIndividual') },
      { value: 'both', label: this.translateService.instant('eventWizard.packages.typeBoth') }
    ];
    if (this.event) {
      // Initialize currency from event's default_currency
      if (this.event.default_currency) {
        this.currency = this.event.default_currency;
      }
      this.loadData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['event'] && this.event) {
      // Update currency from event's default_currency
      if (this.event.default_currency) {
        this.currency = this.event.default_currency;
      }
      this.loadData();
    }
  }

  loadData() {
    if (!this.event) return;
    this.loadPackages();
    this.loadDayCapacities();
  }

  loadDayCapacities() {
    if (!this.event) return;

    this.trainingService.getDayCapacity({ event_id: this.event.event_id }).subscribe({
      next: (capacities) => {
        this.dayCapacities = capacities;
        this.initializePackageDays();
      },
      error: (err) => {
        console.error('Error loading day capacities:', err);
        this.dayCapacities = [];
        this.initializePackageDays();
      }
    });
  }

  initializePackageDays() {
    if (!this.event?.start_date || !this.event?.end_date) {
      return;
    }

    // Parse dates - handle both date strings and datetime strings
    const startDate = this.parseDate(this.event.start_date);
    const endDate = this.parseDate(this.event.end_date);

    if (!startDate || !endDate) {
      return;
    }

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    this.packageDays = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      // Format date as YYYY-MM-DD without timezone conversion
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const dayName = dayNames[currentDate.getDay()];
      const dayNum = currentDate.getDate();
      const monthName = currentDate.toLocaleString('default', { month: 'short' });

      // Find capacity for this date - normalize both dates to YYYY-MM-DD format
      const capacity = this.dayCapacities.find(c => {
        const capacityDateNormalized = this.normalizeDateToISO(c.training_date);
        return capacityDateNormalized === dateStr;
      });
      const hasCapacity = capacity ? (capacity.max_group_sessions > 0 || capacity.max_individual_sessions > 0) : false;

      this.packageDays.push({
        training_date: dateStr,
        dayLabel: `${dayName}, ${monthName} ${dayNum}`,
        session_count: 0,
        hasCapacity
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  normalizeDateToISO(dateValue: string | null | undefined): string | null {
    if (!dateValue) return null;

    const dateStr = String(dateValue);

    // Already in ISO format: "2026-01-03" or "2026-01-03T..."
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
      return dateStr.split('T')[0];
    }

    // US format: "01/03/2026, 09:58 AM" or "01/03/2026"
    if (dateStr.includes('/')) {
      const datePart = dateStr.split(',')[0].trim();
      const parts = datePart.split('/');
      if (parts.length === 3) {
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
    }

    return null;
  }

  parseDate(dateValue: string | Date | null | undefined): Date | null {
    if (!dateValue) return null;

    // If it's already a Date object
    if (dateValue instanceof Date) {
      return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
    }

    // If it's a string, extract just the date part
    const dateStr = String(dateValue);

    // Handle US format: "01/03/2026, 09:58 AM" or "01/03/2026"
    if (dateStr.includes('/')) {
      const datePart = dateStr.split(',')[0].trim(); // Remove time part
      const parts = datePart.split('/');
      if (parts.length === 3) {
        const month = +parts[0];
        const day = +parts[1];
        const year = +parts[2];
        return new Date(year, month - 1, day);
      }
    }

    // Handle ISO format: "2026-01-03T08:00:00" or "2026-01-03"
    // Also handle space separator: "2026-01-03 08:00:00"
    const datePart = dateStr.split('T')[0].split(' ')[0];
    const parts = datePart.split('-');

    if (parts.length === 3) {
      return new Date(+parts[0], +parts[1] - 1, +parts[2]);
    }

    // Fallback: try native parsing
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }

    return null;
  }

  loadPackages() {
    if (!this.event) return;

    this.loading = true;
    this.trainingService.getPackages({ event_id: this.event.event_id }).subscribe({
      next: (packages) => {
        this.packages = packages;
        this.packagesChanged.emit(packages.length);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  openAddModal() {
    this.resetForm();
    this.editingPackage = null;
    // Always reload capacities when opening modal to ensure fresh data
    if (this.event) {
      this.loadDayCapacities();
    }
    this.showAddModal = true;
  }

  openEditModal(pkg: TrainingPackageResponse) {
    this.editingPackage = pkg;
    this.packageName = pkg.package_name;
    this.packageDescription = pkg.package_description || '';
    this.sessionCount = pkg.session_count;
    this.sessionType = pkg.session_type;
    this.priceCents = pkg.price_cents;
    this.priceDisplay = pkg.price_cents > 0 ? (pkg.price_cents / 100).toFixed(2) : '';
    this.currency = this.event?.default_currency || pkg.currency || 'NOK';
    this.vatRate = pkg.vat_rate;
    this.maxRegistrations = pkg.max_registrations || null;
    this.isFree = pkg.price_cents === 0;

    // Load existing package days - normalize dates for comparison
    this.initializePackageDays();
    if (pkg.package_days && pkg.package_days.length > 0) {
      for (const pkgDay of pkg.package_days) {
        const pkgDayDateNormalized = this.normalizeDateToISO(pkgDay.training_date);
        const day = this.packageDays.find(d => d.training_date === pkgDayDateNormalized);
        if (day) {
          day.session_count = pkgDay.session_count;
        }
      }
    }

    this.showAddModal = true;
  }

  closeModal() {
    this.showAddModal = false;
    this.resetForm();
  }

  resetForm() {
    this.packageName = '';
    this.packageDescription = '';
    this.sessionCount = 1;
    this.sessionType = 'group';
    this.priceCents = 0;
    this.priceDisplay = '';
    this.currency = this.event?.default_currency || 'NOK';
    this.vatRate = 0;
    this.maxRegistrations = null;
    this.editingPackage = null;
    this.isFree = false;
    // Reset session counts on existing package days instead of rebuilding
    if (this.packageDays.length > 0) {
      this.packageDays.forEach(d => d.session_count = 0);
    } else {
      this.initializePackageDays();
    }
  }

  getTotalDaySessions(): number {
    return this.packageDays.reduce((sum, d) => sum + d.session_count, 0);
  }

  getDaysWithCapacity(): PackageDayForm[] {
    return this.packageDays.filter(d => d.hasCapacity);
  }

  onPriceChange(value: string) {
    this.priceDisplay = value;
    const numValue = parseFloat(value.replace(',', '.'));
    this.priceCents = isNaN(numValue) ? 0 : Math.round(numValue * 100);
  }

  toggleFree(checked: boolean) {
    this.isFree = checked;
    if (checked) {
      this.priceCents = 0;
      this.priceDisplay = '';
      this.vatRate = 0;
    }
  }

  savePackage() {
    if (!this.event || !this.packageName || this.sessionCount < 1) return;

    const packageDaysData = this.packageDays
      .filter(d => d.session_count > 0)
      .map(d => ({ training_date: d.training_date, session_count: d.session_count }));

    if (this.editingPackage) {
      this.trainingService.updatePackage({
        package_id: this.editingPackage.package_id,
        package_name: this.packageName,
        package_description: this.packageDescription || undefined,
        session_count: this.sessionCount,
        session_type: this.sessionType,
        price_cents: this.priceCents,
        currency: this.currency,
        vat_rate: this.vatRate,
        max_registrations: this.maxRegistrations || undefined
      }).subscribe({
        next: () => {
          // Save package days if any are configured
          if (packageDaysData.length > 0) {
            this.trainingService.setPackageDays({
              package_id: this.editingPackage!.package_id,
              package_days: packageDaysData
            }).subscribe({
              next: () => {
                this.loadPackages();
                this.closeModal();
              }
            });
          } else {
            this.loadPackages();
            this.closeModal();
          }
        }
      });
    } else {
      this.trainingService.createPackage({
        event_id: this.event.event_id,
        package_name: this.packageName,
        package_description: this.packageDescription || undefined,
        session_count: this.sessionCount,
        session_type: this.sessionType,
        price_cents: this.priceCents,
        currency: this.currency,
        vat_rate: this.vatRate,
        max_registrations: this.maxRegistrations || undefined
      }).subscribe({
        next: (createdPackage) => {
          // Save package days if any are configured
          if (packageDaysData.length > 0 && createdPackage) {
            this.trainingService.setPackageDays({
              package_id: createdPackage.package_id,
              package_days: packageDaysData
            }).subscribe({
              next: () => {
                this.loadPackages();
                this.closeModal();
              }
            });
          } else {
            this.loadPackages();
            this.closeModal();
          }
        }
      });
    }
  }

  deletePackage(pkg: TrainingPackageResponse) {
    const modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.componentInstance.description = this.translateService.instant('eventWizard.packages.deleteConfirm', { name: pkg.package_name });

    modalRef.result.then((result) => {
      if (result) {
        this.trainingService.deletePackage(pkg.package_id).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('eventWizard.packages.deleteSuccess'));
            this.loadPackages();
          },
          error: () => {
            this.toastService.error(this.translateService.instant('eventWizard.packages.deleteError'));
          }
        });
      }
    }).catch(() => {});
  }

  formatPrice(cents: number, currency: string): string {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }

  getSessionTypeLabel(type: string): string {
    const found = this.sessionTypes.find(t => t.value === type);
    return found ? found.label : type;
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return dayNames[date.getDay()];
  }

  /**
   * Calculate net amount after platform + payment fees
   * Stripe: 2.4% + 2.00 NOK, Platform: 2%
   * Net = price × 0.956 - 2.00
   */
  calculateNetAmount(priceCents: number): number {
    if (!priceCents || priceCents <= 0) return 0;
    const price = priceCents / 100;
    const net = price * 0.956 - 2;
    return Math.max(0, Math.round(net * 100) / 100);
  }
}
